import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import { useNavigate } from "react-router";
import Header from "../../components/Header";
import BackButton from "../../components/BackButton";
import {
  createEquipment,
  EquipmentGroup,
  getEquipmentGroups,
} from "../../services/equipmentService";
import { uploadFile } from "../../services/exerciseService";
import SuccessMessage from "../../components/SuccessMessage";
import ErrorMessage from "../../components/ErrorMessage";
import UploadButton from "../../components/UploadButton";
import { equipmentValidationSchema } from "../../validations/equipmentValidations";

const AddEquipment: React.FC = () => {
  const [equipmentGroups, setEquipmentGroups] = useState<EquipmentGroup[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchEquipmentGroups = async () => {
      try {
        const groups = await getEquipmentGroups();
        setEquipmentGroups(groups);
      } catch (error) {
        console.error("Error fetching equipment groups:", error);
        setErrorMessage("Failed to fetch equipment groups");
      }
    };

    fetchEquipmentGroups();
  }, []);

  const handleSubmit = async (values: any, { resetForm }: any) => {
    try {
      const equipmentData = {
        name: values.equipmentItem.trim(),
        groupId: Number(values.groupId),
        type: "single-weight",
        targetMuscles: [],
        availableWeights: [],
        imageUrl: values.uploadedImage || undefined,
      };

      await createEquipment(equipmentData);
      setSuccessMessage("Equipment added successfully!");
      resetForm();
      setTimeout(() => navigate("/equipments"), 2000);
    } catch (error) {
      console.error("Error adding equipment:", error);
      setErrorMessage("Failed to add equipment");
    }
  };

  const handleImageUpload = async (
    file: File | null,
    setFieldValue: (field: string, value: any) => void
  ): Promise<string | null> => {
    try {
      if (!file) {
        setErrorMessage("Please select a valid file.");
        return null;
      }

      const uploadedImage = await uploadFile(file);

      if (uploadedImage?.url) {
        setFieldValue("uploadedImage", uploadedImage.url);
        setErrorMessage(null);
        return uploadedImage.url;
      } else {
        setErrorMessage("Failed to upload the image. Please try again.");
        return null;
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      setErrorMessage("An error occurred during image upload.");
      return null;
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen flex flex-col">
      <Header title="Add Equipment" />
      <div className="w-full max-w-lg bg-white shadow-md rounded-md p-6">
        {errorMessage && (
          <ErrorMessage
            message={errorMessage}
            onClose={() => setErrorMessage(null)}
          />
        )}

        {successMessage && (
          <SuccessMessage
            message={successMessage}
            onClose={() => setSuccessMessage(null)}
          />
        )}

        <div className="flex items-center justify-between mt-4 border-b pb-4">
          <BackButton onClick={() => navigate("/equipments")} />
        </div>

        <Formik
          initialValues={{
            equipmentItem: "",
            groupId: "",
            uploadedImage: null,
          }}
          validationSchema={equipmentValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue, errors, touched }) => {
            console.log(errors);
            return (
              <Form>
                <div className="mb-4">
                  <label
                    htmlFor="equipmentItem"
                    className="block text-gray-700 font-medium mb-2"
                  >
                    Equipment Name
                  </label>
                  <Field
                    id="equipmentItem"
                    name="equipmentItem"
                    maxLength={30}
                    type="text"
                    placeholder="Enter equipment item"
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring focus:ring-blue-500"
                  />
                  {errors.equipmentItem && touched.equipmentItem && (
                    <div className="text-red-500 text-sm mt-1">
                      {errors.equipmentItem}
                    </div>
                  )}
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="groupId"
                    className="block text-gray-700 font-medium mb-2"
                  >
                    Equipment Group
                  </label>
                  <Field
                    as="select"
                    id="groupId"
                    name="groupId"
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring focus:ring-blue-500"
                  >
                    <option value="">Select group</option>
                    {equipmentGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </Field>
                  {errors.groupId && touched.groupId && (
                    <div className="text-red-500 text-sm mt-1">
                      {errors.groupId}
                    </div>
                  )}
                </div>

                <div className="mb-6">
                  <label className="block text-gray-700 font-medium mb-2">
                    Upload Image
                  </label>
                  <UploadButton
                    label="Upload Image"
                    index={0}
                    onUpload={(file) => handleImageUpload(file, setFieldValue)}
                    onRemove={() => setFieldValue("uploadedImage", null)}
                    accept="image/*"
                  />
                  {errors.uploadedImage && touched.uploadedImage && (
                    <div className="text-red-500 text-sm mt-1">
                      {errors.uploadedImage}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full py-2 bg-yellow-600 text-white font-semibold rounded-md hover:bg-yellow-700 transition"
                >
                  Submit
                </button>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default AddEquipment;
